import { useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { Building2, LayoutDashboard, LogOut, Menu, MessageSquare, ReceiptText, UserRound } from 'lucide-react'
import { type ComponentType, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { Drawer } from '../../ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'
import { IconButton } from '../../ui/icon-button'
import { usePageEnterMotion } from '../../ui/page-motion'
import { toast } from '../../ui/toaster'
import { LanguageSwitch, ThemeMenu } from '../shell/preferences-menu'
import { Wordmark } from '../shell/wordmark'
import { PLATFORM_LOGIN, usePlatformLogout, usePlatformSession } from './session'

/** The console's sections: one link each in the top bar (and in the phone menu). */
const NAV: readonly {
  id: 'dashboard' | 'tenants' | 'subscriptions' | 'feedback'
  to: '/platform/dashboard' | '/platform/tenants' | '/platform/subscriptions' | '/platform/feedback'
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
}[] = [
  { id: 'dashboard', to: '/platform/dashboard', icon: LayoutDashboard },
  { id: 'tenants', to: '/platform/tenants', icon: Building2 },
  { id: 'subscriptions', to: '/platform/subscriptions', icon: ReceiptText },
  { id: 'feedback', to: '/platform/feedback', icon: MessageSquare },
]

/** "Super admin": on every console screen, so it is never mistaken for a company's own app. */
export function ConsoleBadge({ className }: { className?: string }) {
  const { t } = useTranslation('platform')
  return (
    <span
      data-slot="console-badge"
      className={cn(
        'inline-flex h-6 shrink-0 items-center rounded-xs border-[3px] border-double border-ink-red px-1.5',
        'text-2xs leading-none font-semibold tracking-[0.08em] whitespace-nowrap text-ink-red uppercase',
        '[&:lang(ar)]:text-xs [&:lang(ar)]:tracking-normal [&:lang(ar)]:normal-case',
        className,
      )}
    >
      {t('badge')}
    </span>
  )
}

function NavLinks({ vertical = false, onNavigate }: { vertical?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('platform')
  return (
    <ul className={cn('flex', vertical ? 'flex-col gap-0.5 p-2' : 'items-center gap-1')}>
      {NAV.map(({ id, to, icon: Icon }) => (
        <li key={id}>
          <Link
            to={to}
            onClick={onNavigate}
            activeProps={{ 'aria-current': 'page', className: 'bg-surface-sunken text-ink' }}
            inactiveProps={{ className: 'text-ink-muted' }}
            className={cn(
              'flex items-center gap-2 rounded-sm px-2.5 text-sm font-medium hover:bg-surface-sunken hover:text-ink',
              vertical ? 'h-9' : 'h-8',
            )}
          >
            <Icon aria-hidden className="size-4 shrink-0" />
            {t(`nav.${id}`)}
          </Link>
        </li>
      ))}
    </ul>
  )
}

export function usePlatformSignOut() {
  const { t } = useTranslation('platform')
  const logout = usePlatformLogout()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return {
    pending: logout.isPending,
    signOut: () =>
      logout.mutate(undefined, {
        // Whatever the server says (the session already expired), the local session is over.
        onSettled: async () => {
          await navigate({ to: PLATFORM_LOGIN, replace: true })
          queryClient.clear()
          toast.success(t('admin.signedOut'))
        },
      }),
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase() || '?'
}

function AdminMenu() {
  const { t } = useTranslation('platform')
  const { admin } = usePlatformSession()
  const navigate = useNavigate()
  const { signOut } = usePlatformSignOut()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('admin.menu', { name: admin.name })}
          className="flex size-8 items-center justify-center rounded-sm border border-rule-strong bg-surface-sunken text-xs font-semibold text-ink hover:border-ink-muted"
        >
          <span aria-hidden>{initials(admin.name)}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="px-2 pt-1.5 pb-2">
          <p className="truncate text-sm font-medium text-ink">{admin.name}</p>
          <p className="truncate text-xs text-ink-muted">
            <bdi>{admin.email}</bdi>
          </p>
          <p className="mt-1 truncate text-xs text-ink-muted">{t(`admin.roles.${admin.role}`)}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<UserRound />} onSelect={() => void navigate({ to: '/platform/profile' })}>
          {t('admin.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem icon={<LogOut />} onSelect={signOut}>
          {t('admin.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * The super-admin console's frame: a full-width top bar (wordmark, the Super admin mark, the
 * console's sections, language, theme, the operator's menu) over the page. No company sidebar: the
 * console is not a company's workspace.
 */
export function ConsoleLayout() {
  const { t } = useTranslation('platform')
  const { t: ts } = useTranslation('shell')
  const [menuOpen, setMenuOpen] = useState(false)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const shownPath = useRouterState({ select: (s) => s.resolvedLocation?.pathname })
  const mainRef = useRef<HTMLElement>(null)
  usePageEnterMotion(mainRef, shownPath)

  // biome-ignore lint/correctness/useExhaustiveDependencies: runs on path change only
  useEffect(() => {
    setMenuOpen(false)
  }, [pathname])

  return (
    <div data-slot="shell" className="flex h-dvh flex-col overflow-hidden bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:start-2 focus:top-2 focus:z-50 focus:rounded-sm focus:bg-surface focus:px-3 focus:py-1.5 focus:text-sm"
      >
        {ts('skipToContent')}
      </a>
      <header
        data-slot="console-bar"
        className="flex h-11 shrink-0 items-center gap-2 border-b border-rule bg-surface px-3 sm:px-4"
      >
        <IconButton
          label={t('nav.menu')}
          icon={<Menu />}
          className="md:hidden"
          onClick={() => setMenuOpen(true)}
          aria-expanded={menuOpen}
        />
        <Link to="/platform/dashboard" aria-label={t('consoleName')} className="flex items-center gap-2.5">
          {/* Phones: the mark alone, so the badge, language and account menu still fit the bar. */}
          <Wordmark compact className="sm:hidden" />
          <Wordmark className="max-sm:hidden" />
        </Link>
        <ConsoleBadge />
        <nav aria-label={t('nav.label')} className="ms-4 hidden min-w-0 md:block">
          <NavLinks />
        </nav>
        <div className="flex-1" />
        <LanguageSwitch />
        <ThemeMenu className="hidden sm:inline-flex" />
        <AdminMenu />
      </header>
      <main ref={mainRef} id="main" tabIndex={-1} className="min-h-0 flex-1 overflow-auto outline-none">
        <Outlet />
      </main>

      <Drawer open={menuOpen} onOpenChange={setMenuOpen} side="start" title={t('nav.label')} bare>
        <div className="flex h-full flex-col bg-surface-sunken">
          <div className="flex items-center gap-2 border-b border-rule px-4 py-3">
            <Wordmark compact />
            <ConsoleBadge />
          </div>
          <nav aria-label={t('nav.label')}>
            <NavLinks vertical onNavigate={() => setMenuOpen(false)} />
          </nav>
        </div>
      </Drawer>
    </div>
  )
}
